import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import { BarChart } from '@mui/x-charts';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { requestJson } from '../lib/api';
import { useMenuPage } from '../hooks/useMenuPage';

const colors = ['#38bdf8', '#22c55e', '#a78bfa', '#f59e0b', '#ef4444', '#14b8a6', '#60a5fa', '#f472b6'];
// Keep the usual types in a stable order, then append any type returned by
// the API.  This is important for imported/legacy values such as
// "Classique + Gravure", which must remain visible in the per-store matrix
// instead of being silently omitted.  Trace is intentionally excluded from
// the effective mounting result and therefore never appears as a column.
const MOUNTING_TYPE_COLUMN_ORDER = [
  'Classique',
  'Easy-Digifit',
  'T.D.',
  'Percées',
  'V.S.',
  'Clip',
  'Classique + Gravure',
];

function formatRate(value) {
  const number = Number(value || 0);
  return `${number.toFixed(1).replace('.', ',')} %`;
}

function formatDateInput(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatMonthLabel(date) {
  const label = date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function getMonthRange(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  return {
    period: `${firstDay.getFullYear()}-${String(firstDay.getMonth() + 1).padStart(2, '0')}`,
    label: formatMonthLabel(firstDay),
    from: formatDateInput(firstDay),
    to: formatDateInput(lastDay),
  };
}

function defaultDateRange() {
  const today = new Date();
  return getMonthRange(today.getFullYear(), today.getMonth() - 1);
}

function getPeriodOptions() {
  const today = new Date();
  // Keep a generous history so older imported periods remain selectable while
  // keeping the control compact and predictable.
  return Array.from({ length: 36 }, (_item, index) =>
    getMonthRange(today.getFullYear(), today.getMonth() - index)
  );
}

function csvEscape(value) {
  const text = String(value ?? '');
  return /[;"\n\r]/.test(text) ? '"' + text.replace(/"/g, '""') + '"' : text;
}

function downloadCsv(filename, rows) {
  const content = rows.map((row) => row.map(csvEscape).join(';')).join('\n');
  const blob = new Blob(['\ufeff' + content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function isEdoiStore(store) {
  return /^EDOI$/i.test(String(store?.storeCode || '').trim())
    || /^EDOI$/i.test(String(store?.storeName || '').trim());
}

// Keep the default store table ordered by the numeric part of the site code
// (AA 01, ODB 02, …) instead of by the full display label.
function compareStoreCodes(a, b) {
  const codeA = String(a?.storeCode || '').trim();
  const codeB = String(b?.storeCode || '').trim();
  const numberA = codeA.match(/\d+/);
  const numberB = codeB.match(/\d+/);

  if (numberA && numberB) {
    const numericDiff = Number(numberA[0]) - Number(numberB[0]);
    if (numericDiff) return numericDiff;
    const prefixA = codeA.replace(/\d+/g, '').trim();
    const prefixB = codeB.replace(/\d+/g, '').trim();
    const prefixDiff = prefixA.localeCompare(prefixB, 'fr', { sensitivity: 'base' });
    if (prefixDiff) return prefixDiff;
  } else if (numberA) {
    return -1;
  } else if (numberB) {
    return 1;
  }

  return (codeA || a?.storeName || '').localeCompare(codeB || b?.storeName || '', 'fr', {
    sensitivity: 'base',
  });
}

const EVOLUTION_GROUPS = [
  { key: 'Easy-Digifit', color: '#38bdf8' },
  { key: 'Classique', color: '#22c55e' },
  { key: 'V.S.', color: '#a78bfa' },
  { key: 'Autres', color: '#f59e0b' },
];

// Résume une réponse /statistics/mounting avec les règles du tableau de
// facturation : un dossier avec Trace, traçabilité ou NC est retiré de son
// type et du total, si bien que la somme des types égale le total facturable.
function summarizeMountingStats(payload) {
  const types = {};
  let traceability = 0;
  let ncaJobs = 0;
  let ncmJobs = 0;
  (payload?.byStore || []).forEach((store) => {
    traceability += Number(store.traceabilityCount || 0);
    ncaJobs += Number(store.ncaCount || 0);
    ncmJobs += Number(store.ncmCount || 0);
    (store.mountingTypes || []).forEach((item) => {
      if (item.type === 'Trace') return;
      types[item.type] = (types[item.type] || 0) + Number(item.conformingCount ?? item.count ?? 0);
    });
  });
  return {
    total: Number(payload?.total || 0),
    adjusted: Number(payload?.adjustedTotal ?? payload?.total ?? 0),
    traceType: Number((payload?.byMountingType || []).find((item) => item.type === 'Trace')?.count || 0),
    traceability,
    ncaJobs,
    ncmJobs,
    types,
  };
}

// Mois affichés dans « Évolution » : les 3 derniers mois pour une période
// mensuelle, sinon chaque mois couvert par la période personnalisée (12 max).
function getEvolutionMonths(range, period) {
  if (!range.from || !range.to) return [];
  const end = new Date(`${range.to}T00:00:00`);
  if (Number.isNaN(end.getTime())) return [];
  if (period) {
    return [2, 1, 0].map((offset) => getMonthRange(end.getFullYear(), end.getMonth() - offset));
  }
  const start = new Date(`${range.from}T00:00:00`);
  if (Number.isNaN(start.getTime()) || start > end) return [];
  const months = [];
  for (let cursor = new Date(end.getFullYear(), end.getMonth(), 1); cursor >= new Date(start.getFullYear(), start.getMonth(), 1) && months.length < 12; cursor.setMonth(cursor.getMonth() - 1)) {
    const month = getMonthRange(cursor.getFullYear(), cursor.getMonth());
    months.unshift({
      ...month,
      from: month.from < range.from ? range.from : month.from,
      to: month.to > range.to ? range.to : month.to,
    });
  }
  return months;
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString('fr-FR');
}

function ReconciliationStep({ value, label, tone }) {
  return (
    <Box sx={{ px: 1.25, py: 0.75, borderRadius: 2, bgcolor: tone === 'result' ? 'primary.main' : 'action.hover', color: tone === 'result' ? 'primary.contrastText' : 'inherit' }}>
      <Typography sx={{ fontWeight: 950, fontSize: 17, lineHeight: 1.2, color: tone === 'out' ? 'warning.main' : 'inherit' }}>
        {formatNumber(value)}
      </Typography>
      <Typography variant="caption" sx={{ fontWeight: 800, opacity: tone === 'result' ? 0.9 : 0.7 }}>
        {label}
      </Typography>
    </Box>
  );
}

function EmptyChart({ label = 'Aucune donnée.' }) {
  return (
    <Box sx={{ height: 280, display: 'grid', placeItems: 'center', color: 'text.secondary' }}>
      {label}
    </Box>
  );
}

export default function StatisticsPage() {
  const menuPage = useMenuPage('/statistiques');
  const initialRange = useMemo(defaultDateRange, []);
  const periodOptions = useMemo(getPeriodOptions, []);
  const [period, setPeriod] = useState(initialRange.period);
  const [range, setRange] = useState({ from: initialRange.from, to: initialRange.to });
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [storeMatrixSort, setStoreMatrixSort] = useState({ key: 'label', direction: 'asc' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const query = new URLSearchParams(range).toString();
      const months = getEvolutionMonths(range, period);
      const [payload, ...monthPayloads] = await Promise.all([
        requestJson(`/statistics/mounting?${query}`),
        ...months.map((month) =>
          month.from === range.from && month.to === range.to
            ? Promise.resolve(null)
            : requestJson(`/statistics/mounting?${new URLSearchParams({ from: month.from, to: month.to }).toString()}`)
        ),
      ]);
      setStats(payload);
      setHistory(months.map((month, index) => ({ ...month, ...summarizeMountingStats(monthPayloads[index] || payload) })));
    } catch (err) {
      setError(err.message);
      setStats(null);
      setHistory([]);
    } finally {
      setLoading(false);
    }
    // period accompagne toujours range : le choix d'un mois met à jour les deux.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const summary = useMemo(() => summarizeMountingStats(stats), [stats]);

  const typeRows = useMemo(
    () => Object.entries(summary.types).filter(([, count]) => count > 0).sort((a, b) => b[1] - a[1]),
    [summary]
  );

  const evolutionData = useMemo(
    () =>
      history.map((month) => {
        const named = EVOLUTION_GROUPS.filter((group) => group.key !== 'Autres').map((group) => group.key);
        return {
          label: month.label,
          total: month.adjusted,
          'Easy-Digifit': month.types['Easy-Digifit'] || 0,
          Classique: month.types.Classique || 0,
          'V.S.': month.types['V.S.'] || 0,
          Autres: Object.entries(month.types).filter(([type]) => !named.includes(type)).reduce((sum, [, count]) => sum + count, 0),
        };
      }),
    [history]
  );

  const previousMonth = period && history.length > 1 ? history[history.length - 2] : null;

  const mountingTypeColumns = useMemo(
    () => {
      const availableTypes = new Set(
        (stats?.byMountingType || []).map((item) => item.type).filter((type) => type !== 'Trace')
      );
      const orderedTypes = MOUNTING_TYPE_COLUMN_ORDER.filter((type) => availableTypes.has(type));
      const additionalTypes = [...availableTypes]
        .filter((type) => !MOUNTING_TYPE_COLUMN_ORDER.includes(type))
        .sort((a, b) => String(a).localeCompare(String(b), 'fr', { sensitivity: 'base' }));
      return [...orderedTypes, ...additionalTypes];
    },
    [stats]
  );

  const storeMountingMatrix = useMemo(
    () => {
      const ncaByStore = new Map(
        (stats?.nonConformities?.byStore || []).map((item) => [
          String(item.storeSiteId || item.storeName || ''),
          item.ncaCount || 0,
        ])
      );
      const ncmByStore = new Map(
        (stats?.nonConformities?.byStore || []).map((item) => [
          String(item.storeSiteId || item.storeName || ''),
          item.ncmCount || 0,
        ])
      );
      return (stats?.byStore || []).map((store) => {
        // The matrix represents effective workshop output: jobs flagged by a
        // traceability report, NCA or NCM are excluded from the mounting type
        // counts as well as from the final total.  Keep the raw count on the
        // API object for the other charts, but display the conforming count in
        // this table so the columns add up to the adjusted total.
        const counts = Object.fromEntries(
          (store.mountingTypes || []).map((item) => [item.type, item.conformingCount ?? item.count])
        );
        const adjustedTotal = Number(store.adjustedTotal ?? store.total ?? 0);
        const ncaCount = ncaByStore.get(String(store.storeSiteId || store.storeName || '')) || 0;
        const ncmCount = ncmByStore.get(String(store.storeSiteId || store.storeName || '')) || 0;
        const ncCount = ncaCount + ncmCount;
        return {
          ...store,
          label: [store.storeCode, store.storeName].filter(Boolean).join(' - ') || 'Magasin non renseigné',
          counts,
          adjustedTotal,
          ncaCount,
          ncmCount,
          ncCount,
          ncRate: store.total ? (ncCount / store.total) * 100 : 0,
        };
      });
    },
    [stats]
  );

  const storeMatrixSortOptions = useMemo(
    () => [
      { key: 'label', label: 'Magasin' },
      ...mountingTypeColumns.map((type) => ({ key: type, label: type })),
      { key: 'traceability', label: 'Traçabilité' },
      { key: 'nca', label: 'NCA' },
      { key: 'ncm', label: 'NCM' },
      { key: 'ncRate', label: '% NC' },
      { key: 'total', label: 'Total' },
    ],
    [mountingTypeColumns]
  );

  const sortedStoreMountingMatrix = useMemo(() => {
    const direction = storeMatrixSort.direction === 'asc' ? 1 : -1;
    return [...storeMountingMatrix].sort((a, b) => {
      // EDOI est volontairement conservé en dernière ligne, même lorsque
      // l'utilisateur trie le tableau sur un indicateur statistique.
      if (isEdoiStore(a) !== isEdoiStore(b)) return isEdoiStore(a) ? 1 : -1;
      if (storeMatrixSort.key === 'label') {
        return direction * compareStoreCodes(a, b);
      }
      const getValue = (store) => {
        if (storeMatrixSort.key === 'total') return Number(store.adjustedTotal ?? store.total ?? 0);
        if (storeMatrixSort.key === 'traceability') return Number(store.traceabilityCount || 0);
        if (storeMatrixSort.key === 'nca') return Number(store.ncaCount || 0);
        if (storeMatrixSort.key === 'ncm') return Number(store.ncmCount || 0);
        if (storeMatrixSort.key === 'ncRate') return Number(store.ncRate || 0);
        return Number(store.counts[storeMatrixSort.key] || 0);
      };
      const aValue = getValue(a);
      const bValue = getValue(b);
      if (aValue !== bValue) return direction * (aValue - bValue);
      return a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' });
    });
  }, [storeMountingMatrix, storeMatrixSort]);

  const handleStoreMatrixSort = (key) => {
    setStoreMatrixSort((current) => ({
      key,
      direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const handleExportCsv = () => {
    const rows = [
      ['Magasin', ...mountingTypeColumns, 'Traçabilité', 'NCA', 'NCM', '% NC', 'Total'],
      ...sortedStoreMountingMatrix.map((store) => [
        store.label,
        ...mountingTypeColumns.map((type) => store.counts[type] || 0),
        store.traceabilityCount || 0,
        store.ncaCount || 0,
        store.ncmCount || 0,
        formatRate(store.ncRate || 0),
        store.adjustedTotal ?? store.total ?? 0,
      ]),
    ];
    downloadCsv('stats-montage-' + (range.from || 'debut') + '-' + (range.to || 'fin') + '.csv', rows);
  };

  const handlePeriodChange = (event) => {
    const nextPeriod = event.target.value;
    const selectedPeriod = periodOptions.find((option) => option.period === nextPeriod);
    if (!selectedPeriod) {
      setPeriod('');
      return;
    }
    setPeriod(selectedPeriod.period);
    setRange({ from: selectedPeriod.from, to: selectedPeriod.to });
  };

  const handleFromChange = (event) => {
    setPeriod('');
    setRange((current) => ({ ...current, from: event.target.value }));
  };

  const handleToChange = (event) => {
    setPeriod('');
    setRange((current) => ({ ...current, to: event.target.value }));
  };

  const matrixTotals = useMemo(() => {
    const totals = { counts: {}, traceability: 0, nca: 0, ncm: 0, raw: 0, adjusted: 0 };
    storeMountingMatrix.forEach((store) => {
      mountingTypeColumns.forEach((type) => {
        totals.counts[type] = (totals.counts[type] || 0) + Number(store.counts[type] || 0);
      });
      totals.traceability += Number(store.traceabilityCount || 0);
      totals.nca += Number(store.ncaCount || 0);
      totals.ncm += Number(store.ncmCount || 0);
      totals.raw += Number(store.total || 0);
      totals.adjusted += Number(store.adjustedTotal ?? store.total ?? 0);
    });
    totals.ncRate = totals.raw ? ((totals.nca + totals.ncm) / totals.raw) * 100 : 0;
    return totals;
  }, [storeMountingMatrix, mountingTypeColumns]);

  const ncJobs = summary.ncaJobs + summary.ncmJobs;
  const overlap = summary.traceType + summary.traceability + ncJobs - (summary.total - summary.adjusted);
  const previousDelta = previousMonth?.adjusted
    ? ((summary.adjusted - previousMonth.adjusted) / previousMonth.adjusted) * 100
    : null;

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="overline" color="primary" sx={{ fontWeight: 900 }}>
            {menuPage.sectionTitle}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>
            {menuPage.pageTitle}
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <TextField
            select
            label="Période"
            value={period}
            onChange={handlePeriodChange}
            size="small"
            InputLabelProps={{ shrink: true }}
            SelectProps={{ native: true }}
            sx={{ minWidth: 170 }}
          >
            <option value="">Période personnalisée</option>
            {periodOptions.map((option) => (
              <option key={option.period} value={option.period}>
                {option.label}
              </option>
            ))}
          </TextField>
          <TextField
            type="date"
            label="Du"
            value={range.from}
            onChange={handleFromChange}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="date"
            label="Au"
            value={range.to}
            onChange={handleToChange}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadStats}>
            Rafraîchir
          </Button>
          <Button
            variant="contained"
            startIcon={<FileDownloadRoundedIcon />}
            onClick={handleExportCsv}
            disabled={!storeMountingMatrix.length}
          >
            Export CSV
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'auto 1fr' }, gap: { xs: 2, md: 4 }, alignItems: 'center' }}>
                <Box>
                  <Typography sx={{ fontWeight: 850 }}>Montages facturables</Typography>
                  <Typography variant="h3" sx={{ fontWeight: 950, lineHeight: 1.1 }}>
                    {formatNumber(summary.adjusted)}
                  </Typography>
                  {previousMonth && (
                    <Typography variant="body2" color="text.secondary">
                      {previousMonth.label} : {formatNumber(previousMonth.adjusted)}
                      {previousDelta !== null && ` (${previousDelta > 0 ? '+' : '−'}${Math.abs(previousDelta).toFixed(0)} %)`}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <ReconciliationStep value={summary.total} label="dossiers envoyés" />
                    <Typography sx={{ fontWeight: 900 }} color="text.secondary">−</Typography>
                    <ReconciliationStep value={summary.traceType} label="Trace (pas de montage)" tone="out" />
                    <Typography sx={{ fontWeight: 900 }} color="text.secondary">−</Typography>
                    <ReconciliationStep value={summary.traceability} label="traçabilité" tone="out" />
                    <Typography sx={{ fontWeight: 900 }} color="text.secondary">−</Typography>
                    <ReconciliationStep
                      value={ncJobs}
                      label={`dossiers avec non-conformité (${summary.ncaJobs} atelier, ${summary.ncmJobs} magasin)`}
                      tone="out"
                    />
                    <Typography sx={{ fontWeight: 900 }} color="text.secondary">=</Typography>
                    <ReconciliationStep value={summary.adjusted} label="facturables" tone="result" />
                  </Stack>
                  {overlap > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      {overlap} dossier{overlap > 1 ? 's cumulent' : ' cumule'} plusieurs motifs : ils ne sont retirés qu'une fois.
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 2 }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  Par type de montage
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Montages facturables : la somme correspond au total ci-dessus et à la ligne Total du tableau.
                </Typography>
                {typeRows.length ? (
                  <Stack spacing={1}>
                    {typeRows.map(([type, count], index) => (
                      <Box key={type} sx={{ display: 'grid', gridTemplateColumns: '160px minmax(0, 1fr) 60px 56px', gap: 1.5, alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 850 }}>{type}</Typography>
                        <Box sx={{ height: 14, borderRadius: 1, bgcolor: 'action.hover', overflow: 'hidden' }}>
                          <Box sx={{ height: '100%', width: `${(count / typeRows[0][1]) * 100}%`, bgcolor: colors[index % colors.length], borderRadius: 1 }} />
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 900, textAlign: 'right' }}>{formatNumber(count)}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right' }}>
                          {formatRate(summary.adjusted ? (count / summary.adjusted) * 100 : 0)}
                        </Typography>
                      </Box>
                    ))}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '160px minmax(0, 1fr) 60px 56px', gap: 1.5, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="body2" sx={{ fontWeight: 950 }}>Total</Typography>
                      <Box />
                      <Typography variant="body2" sx={{ fontWeight: 950, textAlign: 'right' }}>{formatNumber(summary.adjusted)}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right' }}>100 %</Typography>
                    </Box>
                  </Stack>
                ) : (
                  <EmptyChart />
                )}
              </CardContent>
            </Card>

            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  {period ? 'Évolution sur 3 mois' : 'Évolution par mois'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Montages facturables par mois, par type de montage.
                </Typography>
                {evolutionData.length ? (
                  <BarChart
                    height={320}
                    dataset={evolutionData}
                    xAxis={[{ scaleType: 'band', dataKey: 'label' }]}
                    yAxis={[{ min: 0 }]}
                    series={EVOLUTION_GROUPS.map((group) => ({
                      dataKey: group.key,
                      label: group.key,
                      color: group.color,
                      stack: 'total',
                    }))}
                    grid={{ horizontal: true }}
                  />
                ) : (
                  <EmptyChart label="Aucune donnée temporelle." />
                )}
              </CardContent>
            </Card>
          </Box>

          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  Répartition par magasin
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                  <TextField
                    label="Trier par"
                    select
                    SelectProps={{ native: true }}
                    size="small"
                    value={storeMatrixSort.key}
                    onChange={(event) => setStoreMatrixSort((current) => ({ ...current, key: event.target.value }))}
                    sx={{ minWidth: { sm: 180 } }}
                  >
                    {storeMatrixSortOptions.map((option) => (
                      <option key={option.key} value={option.key}>
                        {option.label}
                      </option>
                    ))}
                  </TextField>
                  <TextField
                    label="Ordre"
                    select
                    SelectProps={{ native: true }}
                    size="small"
                    value={storeMatrixSort.direction}
                    onChange={(event) => setStoreMatrixSort((current) => ({ ...current, direction: event.target.value }))}
                    sx={{ minWidth: { sm: 150 } }}
                  >
                    <option value="desc">Plus grand</option>
                    <option value="asc">Plus petit</option>
                  </TextField>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FileDownloadRoundedIcon />}
                    onClick={handleExportCsv}
                    disabled={!storeMountingMatrix.length}
                    sx={{ minHeight: 40 }}
                  >
                    Export CSV
                  </Button>
                </Stack>
              </Stack>
              <TableContainer>
                <Table
                  size="small"
                  sx={{
                    '& .MuiTableSortLabel-root': { fontWeight: 900 },
                    '& .MuiTableSortLabel-icon': { opacity: 0.45 },
                    '& .Mui-active .MuiTableSortLabel-icon': { opacity: 1 },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sortDirection={storeMatrixSort.key === 'label' ? storeMatrixSort.direction : false}>
                        <TableSortLabel
                          active={storeMatrixSort.key === 'label'}
                          direction={storeMatrixSort.key === 'label' ? storeMatrixSort.direction : 'asc'}
                          onClick={() => handleStoreMatrixSort('label')}
                        >
                          Magasin
                        </TableSortLabel>
                      </TableCell>
                      {mountingTypeColumns.map((type) => (
                        <TableCell key={type} align="right" sortDirection={storeMatrixSort.key === type ? storeMatrixSort.direction : false}>
                          <TableSortLabel
                            active={storeMatrixSort.key === type}
                            direction={storeMatrixSort.key === type ? storeMatrixSort.direction : 'asc'}
                            onClick={() => handleStoreMatrixSort(type)}
                          >
                            {type}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                      <TableCell align="right" sortDirection={storeMatrixSort.key === 'traceability' ? storeMatrixSort.direction : false}>
                        <TableSortLabel
                          active={storeMatrixSort.key === 'traceability'}
                          direction={storeMatrixSort.key === 'traceability' ? storeMatrixSort.direction : 'asc'}
                          onClick={() => handleStoreMatrixSort('traceability')}
                        >
                          Traçabilité
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={storeMatrixSort.key === 'nca' ? storeMatrixSort.direction : false}>
                        <TableSortLabel
                          active={storeMatrixSort.key === 'nca'}
                          direction={storeMatrixSort.key === 'nca' ? storeMatrixSort.direction : 'asc'}
                          onClick={() => handleStoreMatrixSort('nca')}
                        >
                          NCA
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={storeMatrixSort.key === 'ncm' ? storeMatrixSort.direction : false}>
                        <TableSortLabel
                          active={storeMatrixSort.key === 'ncm'}
                          direction={storeMatrixSort.key === 'ncm' ? storeMatrixSort.direction : 'asc'}
                          onClick={() => handleStoreMatrixSort('ncm')}
                        >
                          NCM
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={storeMatrixSort.key === 'ncRate' ? storeMatrixSort.direction : false}>
                        <TableSortLabel
                          active={storeMatrixSort.key === 'ncRate'}
                          direction={storeMatrixSort.key === 'ncRate' ? storeMatrixSort.direction : 'asc'}
                          onClick={() => handleStoreMatrixSort('ncRate')}
                        >
                          % NC
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={storeMatrixSort.key === 'total' ? storeMatrixSort.direction : false}>
                        <TableSortLabel
                          active={storeMatrixSort.key === 'total'}
                          direction={storeMatrixSort.key === 'total' ? storeMatrixSort.direction : 'asc'}
                          onClick={() => handleStoreMatrixSort('total')}
                        >
                          Total
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedStoreMountingMatrix.map((store) => (
                      <TableRow key={store.storeSiteId || store.storeName}>
                        <TableCell sx={{ fontWeight: 800 }}>{store.label}</TableCell>
                        {mountingTypeColumns.map((type) => (
                          <TableCell key={type} align="right" sx={{ fontWeight: store.counts[type] ? 850 : 400 }}>
                            {store.counts[type] || 0}
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: store.traceabilityCount ? 850 : 400 }}>
                          {store.traceabilityCount || 0}
                        </TableCell>
                        <TableCell align="right">
                          {store.ncaCount ? (
                            <Chip size="small" color="warning" variant="outlined" label={store.ncaCount} sx={{ minWidth: 34, fontWeight: 950 }} />
                          ) : (
                            0
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {store.ncmCount ? (
                            <Chip size="small" color="warning" variant="outlined" label={store.ncmCount} sx={{ minWidth: 34, fontWeight: 950 }} />
                          ) : (
                            0
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            size="small"
                            color={store.ncRate ? 'warning' : 'default'}
                            variant="outlined"
                            label={formatRate(store.ncRate)}
                            sx={{ minWidth: 58, fontWeight: 950 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            size="small"
                            label={store.adjustedTotal ?? store.total ?? 0}
                            sx={{
                              minWidth: 38,
                              bgcolor: 'rgba(245, 158, 11, 0.18)',
                              border: '1px solid rgba(245, 158, 11, 0.48)',
                              color: '#fbbf24',
                              fontWeight: 950,
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    {sortedStoreMountingMatrix.length > 0 && (
                      <TableRow sx={{ '& td': { fontWeight: 950, borderTop: '2px solid', borderTopColor: 'divider', borderBottom: 0, bgcolor: 'action.hover' } }}>
                        <TableCell>Total réseau</TableCell>
                        {mountingTypeColumns.map((type) => (
                          <TableCell key={type} align="right">
                            {formatNumber(matrixTotals.counts[type])}
                          </TableCell>
                        ))}
                        <TableCell align="right">{formatNumber(matrixTotals.traceability)}</TableCell>
                        <TableCell align="right">{formatNumber(matrixTotals.nca)}</TableCell>
                        <TableCell align="right">{formatNumber(matrixTotals.ncm)}</TableCell>
                        <TableCell align="right">{formatRate(matrixTotals.ncRate)}</TableCell>
                        <TableCell align="right">
                          <Chip
                            size="small"
                            label={formatNumber(matrixTotals.adjusted)}
                            sx={{
                              minWidth: 38,
                              bgcolor: 'rgba(245, 158, 11, 0.18)',
                              border: '1px solid rgba(245, 158, 11, 0.48)',
                              color: '#fbbf24',
                              fontWeight: 950,
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}
    </Stack>
  );
}
